import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import LivelyTalkIcon from '@/images/movements/icons/talkLivelyIcon.svg';
import NaturalTalkIcon from '@/images/movements/icons/talkNaturalIcon.svg';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { PolotnoStoreActivePageKeys, usePolotnoActivePageState } from '@/pages/AdvancedEditor/hooks';
import { usePresenterDrivers } from '@/pages/AdvancedEditor/hooks/usePresenterDrivers';
import { MenuItem } from '@/pages/AdvancedEditor/types';
import { getAvatarPresenterFromPage } from '@/pages/AdvancedEditor/utils';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import { IClipDriver, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { debounce, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { ControlButton } from '../../../common/ControlButton';

const Container = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
`;

const getIcon = (driver: IClipDriver) => {
    if (driver.name?.toLowerCase() === 'natural' || driver.name?.toLowerCase() === 'subtle') {
        return NaturalTalkIcon;
    }

    if (driver.name?.toLowerCase() === 'lively') {
        return LivelyTalkIcon;
    }

    return driver.thumbnail_url;
};

export const MovementsControl = observer(() => {
    const { store } = useAdvancedEditorContext();
    const presenter = getAvatarPresenterFromPage(store.activePage);
    const { presenterDrivers } = usePresenterDrivers(presenter);
    const [selectedDriver, setSelectedDriver] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.selectedDriver);
    const mixpanel = useAnalytics();

    const handleChange = (driver: string) => {
        mixpanel?.track('emotion', { event: 'click' });
        setSelectedDriver(driver);
        updateStoreDriver(driver);
    };

    // Update store with debounce only to trigger store change event and save artifacts
    const updateStoreDriver = useCallback(
        debounce((driver: string) => {
            store.activePage?.set({
                custom: {
                    ...store.activePage.custom,
                    driver,
                },
            });
        }, 1000),
        [store.activePage]
    );

    const controls: MenuItem[] = presenterDrivers.map((driver, index) => ({
        key: driver.driver_id,
        srcValue: getIcon(driver),
        name: driver.name || `Driver #${index + 1}`,
        type: driver.type ?? VideoType.CLIP,
    }));

    return (
        <Container>
            {controls.map((control, index) => (
                <Tooltip
                    key={control.key}
                    title={control.name}
                    placement="top"
                    slotProps={{ tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } } }}
                    PopperProps={{
                        modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
                    }}>
                    <ControlButton
                        name={control.name}
                        onClick={() => handleChange(control.key)}
                        selected={selectedDriver ? control.key === selectedDriver : index === 0}>
                        <MaskedIcon src={control.srcValue} size="20px" />
                    </ControlButton>
                </Tooltip>
            ))}
        </Container>
    );
});
